import { generatePath, useNavigate } from 'react-router-dom'
import { FatecieIcon } from '@/assets/icons/fatecie-icon'
import { CancelRequestModal } from '@/components/dialogs/cancel-request'
import { ErrorBoundary } from '@/components/error-boundary'
import { Show } from '@/components/show'
import { Stepper } from '@/components/stepper'
import { Button } from '@/components/ui/button'
import { ROUTES } from '@/constants/routes'
import type { FileUploadStatus } from '@/types/file-upload/file-upload-status'
import type { Request } from '@/types/requests/request'

interface Steps {
  label: string
  content: React.ReactNode
  pendingSave: boolean
}

interface StudentRequestContentProps {
  steps: Steps[]
  currentStep: number
  handleNext: (e: React.FormEvent) => void
  handleBack: () => void
  requestTitle: string
  requestSubTitle?: string
  transferCourse?: string
  fileStatus: FileUploadStatus
  request?: Request
  finishedTransfer?: boolean
  disabledButtons?: boolean
}

export const StudentRequestContent = ({
  steps,
  currentStep,
  handleNext,
  handleBack,
  requestTitle,
  requestSubTitle,
  transferCourse,
  finishedTransfer,
  fileStatus,
  request,
  disabledButtons
}: StudentRequestContentProps) => {
  const navigate = useNavigate()

  const step = steps[currentStep - 1]
  const isPendingSave = !!step?.pendingSave

  const isLast = currentStep === steps.length

  const goToMenu = () => {
    navigate(`/${ROUTES.STUDENT_HOME.route}`)
  }

  const goToRequest = () => {
    if (request) {
      const path = generatePath(ROUTES.STUDENT_MY_REVIEWS.route, {
        id: request.id
      })
      navigate(`/${path}`)
    }
  }

  return (
    <>
      <h1 className="text-2xl font-bold">{requestTitle}</h1>
      <form onSubmit={handleNext}>
        <div className="flex items-center py-2 justify-between">
          <h1 className="text-[var(--brand-blue-5)]">
            {requestSubTitle ? (
              <p>
                <strong> {requestSubTitle}: </strong>
                {transferCourse}
              </p>
            ) : (
              ''
            )}
          </h1>
          <FatecieIcon />
        </div>

        <div className="border-b border-muted-foreground/60 mb-10 w-full"></div>

        <Stepper currentStep={currentStep} steps={steps} />

        <div className="flex flex-col mx-auto mt-4 w-280">
          <div className="border-b border-muted-foreground/60"></div>
          <ErrorBoundary scope="request-step">{step?.content}</ErrorBoundary>
        </div>

        <div className="border-b border-muted-foreground/60 p-6"></div>

        {!disabledButtons && (
          <div className="flex py-4">
            <Button variant="backButton" type="button" onClick={handleBack}>
              Voltar
            </Button>
            <div className="px-6">
              <CancelRequestModal cancelAction={goToMenu} />
            </div>
            <div className="flex w-full gap-5 justify-end">
              <Show
                when={
                  fileStatus === 'warning' && !!request?.id && currentStep === 1
                }
              >
                <Button
                  type="submit"
                  className="w-40 underline cursor-pointer"
                  variant={'link'}
                  disabled={fileStatus === 'pending' || isPendingSave}
                >
                  Deseja seguir?
                </Button>
              </Show>
              <Button
                disabled={
                  isPendingSave
                  || !request?.id
                  || ((fileStatus === 'error'
                    || fileStatus === 'warning'
                    || fileStatus === 'pending')
                    && currentStep === 1)
                }
                isLoading={isPendingSave}
                variant="nextButton"
                type="submit"
              >
                {isLast ? 'Concluir' : 'Avançar'}
              </Button>
            </div>
          </div>
        )}

        {finishedTransfer && disabledButtons && (
          <div className="flex py-4 gap-2 justify-center">
            <Button
              onClick={goToRequest}
              className="w-50 font-bold"
              type="button"
              variant={'light'}
            >
              Ir para os detalhes
            </Button>
            <Button
              className="underline w-50"
              type="button"
              variant={'link'}
              onClick={goToMenu}
            >
              Voltar ao Menu
            </Button>
          </div>
        )}
      </form>
    </>
  )
}
